import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, X, HelpCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';
import { formatAura } from '../lib/aura';
import { relativeTime } from '../lib/social';

const TABS = [
  { k: 'received',  key: '/recognitions/incoming' },
  { k: 'given',     key: '/recognitions/outgoing' },
  { k: 'witness',   key: '/recognitions/witness' },
];

const LABEL_ES = { received: 'Aura recibida', given: 'Aura otorgada', witness: 'Como testigo' };
const STATUS_ES = { pending_witnesses: 'Esperando testigos', awaiting_validation: 'Esperando validación', approved: 'Aprobada', rejected: 'Rechazada', expired: 'Expirada', flagged: 'En revisión', cancelled: 'Cancelada', reversed: 'Revertida' };

export default function CommunityAuraPage() {
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState('witness');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondFor, setRespondFor] = useState(null);
  const [respBusy, setRespBusy] = useState(false);
  const [respEthical, setRespEthical] = useState(false);

  const load = async () => {
    setLoading(true);
    const cur = TABS.find((x) => x.k === tab);
    try {
      const params = tab === 'witness' ? { status: 'pending' } : {};
      const { data } = await api.get(cur.key, { params });
      setItems(data.items || []);
    } catch (_e) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const respond = async (response) => {
    if (respBusy) return;
    if (response === 'confirmed' && !respEthical) { toast.error('Acepta la declaración del testigo'); return; }
    setRespBusy(true);
    try {
      await api.post(`/recognitions/${respondFor.recognition_id}/respond`, { response, ethical_declaration: respEthical });
      toast.success(response === 'confirmed' ? 'Validación confirmada' : response === 'denied' ? 'Marcado como no presenciado' : 'Registrado');
      setRespondFor(null); setRespEthical(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); }
    finally { setRespBusy(false); }
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1"><ChevronLeft size={20} /></button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">COMMUNITY AURA</div>
        <span className="w-8" />
      </header>

      <div className="mx-auto max-w-md px-5 pt-3 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-aura-yellow">Tu Aura es tu reputación</div>
        <div className="text-xs text-white/70 mt-1">Reconoce acciones reales. No regales puntos. El Aura se gana.</div>
      </div>

      <div className="mx-auto max-w-md mt-4 border-b border-white/5">
        <div className="flex gap-4 px-5">
          {TABS.map((x) => (
            <button key={x.k} onClick={() => setTab(x.k)} data-testid={`tab-${x.k}`}
                    className={`pb-2 text-xs font-display font-bold tracking-widest ${tab === x.k ? 'text-aura-cyan border-b-2 border-aura-cyan' : 'text-aura-textDim'}`}>
              {LABEL_ES[x.k]}
            </button>
          ))}
        </div>
      </div>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-3">
        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim">Sin reconocimientos aún.</div>
        ) : items.map((r) => (
          <div key={r.recognition_id} className="aura-card p-4" data-testid={`rec-${r.recognition_id}`}>
            <div className="flex items-center gap-3 mb-2">
              <UserAvatar user={tab === 'witness' || tab === 'received' ? r.giver : r.receiver} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-display font-semibold truncate">
                  {tab === 'received' && <>de @{r.giver?.username}</>}
                  {tab === 'given' && <>para @{r.receiver?.username}</>}
                  {tab === 'witness' && <>@{r.giver?.username} → @{r.receiver?.username}</>}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-aura-textDim">{r.category} · {relativeTime(r.created_at, locale)}</div>
              </div>
              <div className="text-right">
                <div className="text-aura-cyan font-mono font-bold text-lg">+{r.proposed_aura_points}</div>
                <div className="text-[9px] uppercase tracking-widest text-aura-textDim">AURA</div>
              </div>
            </div>
            <p className="text-sm text-white/90 mb-2">"{r.description}"</p>
            <div className="flex items-center justify-between text-[11px]">
              <span className={`font-display font-bold ${r.status === 'approved' ? 'text-aura-green' : r.status === 'rejected' ? 'text-aura-pink' : 'text-aura-textDim'}`}>
                {STATUS_ES[r.status] || r.status}
              </span>
              <span className="text-aura-textDim">
                {r.confirmed_count}✓ / {r.denied_count}✗ / {r.pending_count}⏳
              </span>
            </div>

            {tab === 'witness' && r.viewer?.is_witness && r.viewer.my_response === 'pending' && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <div className="text-[11px] uppercase tracking-widest text-aura-yellow mb-2">¿Presenciaste esta acción?</div>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => setRespondFor(r)} data-testid={`open-respond-${r.recognition_id}`} className="aura-btn-primary !py-2 !text-xs">
                    <Check size={12} /> Sí, la presencié
                  </button>
                  <button onClick={async () => { try { await api.post(`/recognitions/${r.recognition_id}/respond`, { response: 'denied', ethical_declaration: true }); load(); } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); } }}
                          data-testid={`deny-${r.recognition_id}`}
                          className="aura-btn-ghost !py-2 !text-xs !border-red-400/40 text-red-400">
                    <X size={12} /> No la presencié
                  </button>
                  <button onClick={async () => { try { await api.post(`/recognitions/${r.recognition_id}/respond`, { response: 'unable_to_confirm', ethical_declaration: false }); load(); } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); } }}
                          data-testid={`unable-${r.recognition_id}`}
                          className="aura-btn-ghost !py-2 !text-xs">
                    <HelpCircle size={12} /> No puedo
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </main>

      {/* Respond confirm modal */}
      {respondFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur px-5" data-testid="witness-declaration-modal">
          <div className="w-full max-w-md aura-card p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-aura-yellow mb-2">Declaración del Testigo</div>
            <p className="text-sm text-white/90 mb-4">
              Al confirmar esta acción declaras que realmente la presenciaste o puedes acreditar de manera directa que ocurrió.
              Validar hechos falsos o participar en acuerdos para inflar Aura Points constituye una violación al Movimiento Aura.
            </p>
            <label className="flex items-start gap-2 text-sm mb-4">
              <input type="checkbox" data-testid="witness-ethical" checked={respEthical} onChange={(e) => setRespEthical(e.target.checked)} className="mt-0.5 h-4 w-4 accent-aura-cyan" />
              <span>Confirmo que mi validación es verdadera.</span>
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setRespondFor(null); setRespEthical(false); }} className="aura-btn-ghost !py-2 !px-4 !text-sm">Cancelar</button>
              <button disabled={!respEthical || respBusy} onClick={() => respond('confirmed')} data-testid="witness-confirm-final" className="aura-btn-primary !py-2 !px-4 !text-sm disabled:opacity-50">
                Confirmar validación
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
